import { HttpError } from "./errors";

/**
 * Read a non-2xx body from a `/chat/...` route into `{ message, code }`. Those
 * routes answer with `chatErrorBody()` (src/errors.ts), which puts the prose in
 * `error` and the machine code in `code`. That is the opposite of the
 * `/conversations` routes, which is why `conversations.ts` keeps its own reader.
 *
 * It lives here and not in `client.ts` because ten test files mock
 * `../api/client` with only `sendChat` and `stopChat`. Bun mocks are
 * process-global, so a module that imported `readError` from `client.ts` and
 * was linked after such a mock would fail with "Export named 'readError' not
 * found". `client.ts` re-exports it for its existing importers.
 */
export async function readError(res: Response): Promise<{ message: string; code?: string; reason?: string }> {
  try {
    const data = (await res.json()) as { error?: unknown; code?: unknown; reason?: unknown };
    const message = typeof data.error === "string" && data.error.length > 0 ? data.error : `http ${res.status}`;
    const code = typeof data.code === "string" ? data.code : undefined;
    return typeof data.reason === "string" ? { message, code, reason: data.reason } : { message, code };
  } catch {
    return { message: `http ${res.status}` };
  }
}

/**
 * The fetch wrapper for JSON `/chat/...` routes that are not SSE. An abort is
 * rethrown as the original AbortError: the queue's long wait is aborted on
 * purpose, and a caller has to be able to tell that from a network failure.
 */
export async function doFetch(url: string, init: RequestInit): Promise<Response> {
  let res: Response;
  try {
    res = await fetch(url, init);
  } catch (e) {
    if ((e as Error)?.name === "AbortError" || init.signal?.aborted) throw e;
    throw new Error(`network error: ${(e as Error).message}`);
  }
  if (res.status === 401) throw new HttpError(401, "unauthorized");
  if (!res.ok) {
    const { message, code, reason } = await readError(res);
    throw new HttpError(res.status, message, code, reason !== undefined ? { reason } : undefined);
  }
  return res;
}
